import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TitleAndInfo from './TitleAndInfo';
import {
    Typography, Box, Container, IconButton, Paper, useTheme,
    CircularProgress, Chip, useMediaQuery, TextField, InputAdornment,
    MenuItem, Checkbox, Tooltip, Collapse, Badge, Divider, Button, Dialog, Popover,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
    TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
    Search as SearchIcon, FilterList as FilterListIcon,
    Today as TodayIcon, Close as CloseIcon, AccessTime as AccessTimeIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon,
    CalendarMonth as CalendarMonthIcon, Star as StarIcon,
    WbSunny as WbSunnyIcon, DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import axios from '../config/axiosConfig';

const EPS_STATUSES = ['Beat', 'In-line', 'Miss', 'Pending'];
const MARKET_CAP_BUCKETS = [
    { label: 'Mega (>$200B)', min: 200e9 },
    { label: 'Large ($10B-$200B)', min: 10e9, max: 200e9 },
    { label: 'Mid ($2B-$10B)', min: 2e9, max: 10e9 },
    { label: 'Small (<$2B)', max: 2e9 },
];
const MEGA_CAP_THRESHOLD = 200e9;

const formatDateKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
};

const formatMobileDate = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
    return `${sign}$${abs}`;
};

const formatEps = (value) => {
    if (value === null || value === undefined) return '-';
    return `$${value.toFixed(2)}`;
};

const getNextValidDate = (date, direction) => {
    const newDate = new Date(date);
    do { newDate.setDate(newDate.getDate() + direction); }
    while (newDate.getDay() === 0 || newDate.getDay() === 6);
    return newDate;
};

const getWeekDates = (date) => {
    const dates = [];
    const currentDate = new Date(date);
    while (currentDate.getDay() !== 1) currentDate.setDate(currentDate.getDate() - 1);
    for (let i = 0; i < 5; i++) {
        const newDate = new Date(currentDate);
        dates.push({ date: newDate, formatted: formatDate(newDate), fullDate: formatDateKey(newDate) });
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
};

const isTodayDate = (date) => formatDateKey(new Date()) === formatDateKey(date);

const getEpsSurprise = (eps, epsEstimated) => {
    if (eps === null || eps === undefined || epsEstimated === null || epsEstimated === undefined) return null;
    return +(eps - epsEstimated).toFixed(4);
};

const getRevenueSurprise = (revenue, revenueEstimated) => {
    if (revenue === null || revenue === undefined || revenueEstimated === null || revenueEstimated === undefined) return null;
    return revenue - revenueEstimated;
};

const getRevenueSurprisePercent = (revenue, revenueEstimated) => {
    if (!revenue || !revenueEstimated || revenueEstimated === 0) return null;
    return +(((revenue - revenueEstimated) / Math.abs(revenueEstimated)) * 100).toFixed(2);
};

const getEpsSurprisePercent = (eps, epsEstimated) => {
    if (eps === null || eps === undefined || epsEstimated === null || epsEstimated === undefined || epsEstimated === 0) return null;
    return +(((eps - epsEstimated) / Math.abs(epsEstimated)) * 100).toFixed(2);
};

const getSurpriseStatus = (eps, epsEstimated, revenue) => {
    if (eps === null || eps === undefined) return 'Pending';
    if (eps === 0 && (revenue === 0 || revenue === null || revenue === undefined)) return 'Pending';
    if (epsEstimated === null || epsEstimated === undefined) return 'Pending';
    const diff = eps - epsEstimated;
    if (Math.abs(diff) < 0.005) return 'In-line';
    return diff > 0 ? 'Beat' : 'Miss';
};

const isMegaCap = (marketCap) => marketCap && marketCap >= MEGA_CAP_THRESHOLD;

const getTimeLabel = (time) => {
    if (time === 'bmo') return 'BMO';
    if (time === 'amc') return 'AMC';
    return time || '-';
};

const SurpriseChip = ({ actual, estimated, format = 'eps', theme, isPending = false }) => {
    if (isPending || actual === null || actual === undefined) {
        return <Chip label="Pending" size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }} />;
    }
    if (estimated === null || estimated === undefined) {
        return <Typography variant="body2" color="text.secondary">-</Typography>;
    }
    const diff = actual - estimated;
    const pct = estimated !== 0 ? ((diff / Math.abs(estimated)) * 100).toFixed(1) : null;
    const isInline = Math.abs(diff) < (format === 'eps' ? 0.005 : 1000);
    const isBeat = diff > 0;
    let label;
    if (isInline) { label = 'In-line'; }
    else if (format === 'eps') { label = `${isBeat ? '+' : ''}$${diff.toFixed(2)}${pct ? ` (${pct}%)` : ''}`; }
    else { label = `${isBeat ? '+' : ''}${formatCurrency(diff)}${pct ? ` (${pct}%)` : ''}`; }
    const color = isInline ? theme.palette.text.secondary : isBeat ? theme.palette.success.main : theme.palette.error.main;
    const bgColor = isInline ? `${theme.palette.text.secondary}12` : isBeat ? `${theme.palette.success.main}18` : `${theme.palette.error.main}18`;
    return <Chip label={label} size="small" sx={{ backgroundColor: bgColor, color, fontWeight: 600, fontSize: '0.72rem', height: 24, border: `1px solid ${color}30` }} />;
};

const TimeBadge = ({ time, theme }) => {
    const isBMO = time === 'bmo';
    const isAMC = time === 'amc';
    const color = isBMO ? theme.palette.warning.main : isAMC ? theme.palette.info.main : theme.palette.text.secondary;
    const icon = isBMO ? <WbSunnyIcon sx={{ fontSize: '0.8rem !important' }} /> : isAMC ? <DarkModeIcon sx={{ fontSize: '0.8rem !important' }} /> : null;
    return <Chip icon={icon} label={getTimeLabel(time)} size="small" sx={{ backgroundColor: `${color}18`, color, fontWeight: 700, fontSize: '0.7rem', height: 22, border: `1px solid ${color}30`, '& .MuiChip-icon': { ml: '4px', color } }} />;
};

const SummaryStatsBar = ({ data, theme, isMobile }) => {
    const stats = useMemo(() => {
        let reported = 0, beat = 0, miss = 0, inline = 0, pending = 0;
        data.forEach((item) => {
            const status = getSurpriseStatus(item.eps, item.epsEstimated, item.revenue);
            if (status === 'Pending') pending++;
            else { reported++; if (status === 'Beat') beat++; else if (status === 'Miss') miss++; else inline++; }
        });
        return { total: data.length, reported, beat, miss, inline, pending };
    }, [data]);
    const items = [
        { label: 'Total', value: stats.total, color: theme.palette.text.primary },
        { label: 'Reported', value: stats.reported, color: theme.palette.primary.main },
        { label: 'Beat', value: stats.beat, color: theme.palette.success.main },
        { label: 'In-line', value: stats.inline, color: theme.palette.text.secondary },
        { label: 'Miss', value: stats.miss, color: theme.palette.error.main },
        { label: 'Pending', value: stats.pending, color: theme.palette.warning.main },
    ];
    return (
        <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap', justifyContent: isMobile ? 'space-around' : 'flex-start' }}>
            {items.map((item) => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: item.color, fontSize: isMobile ? '1rem' : '1.1rem' }}>{item.value}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</Typography>
                </Box>
            ))}
        </Box>
    );
};

const DetailItem = ({ label, value, color }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: color || 'text.primary' }}>{value}</Typography>
    </Box>
);

const MobileEventCard = ({ event, theme }) => {
    const [expanded, setExpanded] = useState(false);
    const isPending = getSurpriseStatus(event.eps, event.epsEstimated, event.revenue) === 'Pending';
    const status = getSurpriseStatus(event.eps, event.epsEstimated, event.revenue);
    const isMega = isMegaCap(event.marketCap);
    const statusColor = status === 'Beat' ? theme.palette.success.main : status === 'Miss' ? theme.palette.error.main : status === 'In-line' ? theme.palette.text.secondary : theme.palette.warning.main;
    return (
        <Paper variant="stats" sx={{ mb: 1.5, overflow: 'hidden', opacity: isPending ? 0.75 : 1, borderLeft: `3px solid ${statusColor}`, ...(isMega && { border: `1px solid ${theme.palette.warning.main}40`, borderLeft: `3px solid ${theme.palette.warning.main}` }) }}>
            <Box onClick={() => setExpanded(!expanded)} sx={{ p: 1.5, cursor: 'pointer', '&:active': { backgroundColor: 'action.hover' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{event.symbol}</Typography>
                        {isMega && <Tooltip title="Mega Cap" arrow><StarIcon sx={{ fontSize: '0.9rem', color: theme.palette.warning.main }} /></Tooltip>}
                        <TimeBadge time={event.time} theme={theme} />
                    </Box>
                    <Chip label={event.exchange} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontStyle: isPending ? 'italic' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {event.name}{isPending && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontStyle: 'normal' }}>(Pending)</Typography>}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {!isPending && <Typography variant="caption"><b>EPS:</b> {formatEps(event.eps)}</Typography>}
                    <Typography variant="caption" color="text.secondary"><b>Est:</b> {formatEps(event.epsEstimated)}</Typography>
                    <SurpriseChip actual={event.eps} estimated={event.epsEstimated} format="eps" theme={theme} isPending={isPending} />
                    <Typography variant="caption" color="text.secondary">{formatCurrency(event.marketCap)}</Typography>
                    <Box sx={{ ml: 'auto' }}>{expanded ? <KeyboardArrowUpIcon fontSize="small" color="action" /> : <KeyboardArrowDownIcon fontSize="small" color="action" />}</Box>
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <DetailItem label="EPS Actual" value={formatEps(event.eps)} />
                    <DetailItem label="EPS Estimate" value={formatEps(event.epsEstimated)} />
                    <DetailItem label="EPS Reported" value={isPending ? 'Pending' : event.eps != null && event.epsEstimated != null ? `${getEpsSurprise(event.eps, event.epsEstimated) > 0 ? '+' : ''}$${getEpsSurprise(event.eps, event.epsEstimated).toFixed(2)} (${getEpsSurprisePercent(event.eps, event.epsEstimated)}%)` : '-'} color={!isPending && getEpsSurprise(event.eps, event.epsEstimated) > 0 ? theme.palette.success.main : !isPending && getEpsSurprise(event.eps, event.epsEstimated) < 0 ? theme.palette.error.main : undefined} />
                    <DetailItem label="Revenue" value={formatCurrency(event.revenue)} />
                    <DetailItem label="Rev. Estimate" value={formatCurrency(event.revenueEstimated)} />
                    <DetailItem label="Rev. Reported" value={isPending ? 'Pending' : event.revenue != null && event.revenueEstimated != null ? `${getRevenueSurprise(event.revenue, event.revenueEstimated) > 0 ? '+' : ''}${formatCurrency(getRevenueSurprise(event.revenue, event.revenueEstimated))} (${getRevenueSurprisePercent(event.revenue, event.revenueEstimated)}%)` : '-'} color={!isPending && getRevenueSurprise(event.revenue, event.revenueEstimated) > 0 ? theme.palette.success.main : !isPending && getRevenueSurprise(event.revenue, event.revenueEstimated) < 0 ? theme.palette.error.main : undefined} />
                    <DetailItem label="Market Cap" value={formatCurrency(event.marketCap)} />
                    <DetailItem label="Price" value={event.price ? `$${event.price.toFixed(2)}` : '-'} />
                    <DetailItem label="Exchange" value={event.exchange} />
                    <DetailItem label="Fiscal End" value={event.fiscalDateEnding ? new Date(event.fiscalDateEnding).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'} />
                </Box>
            </Collapse>
        </Paper>
    );
};

const DatePickerDialog = ({ open, onClose, onSelect, selectedDate, earliestDate, latestDate, theme }) => {
    const [viewMonth, setViewMonth] = useState(new Date(selectedDate));
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const handleSelect = (day) => {
        const newDate = new Date(year, month, day);
        if (newDate.getDay() === 0 || newDate.getDay() === 6) return;
        if (earliestDate && newDate < new Date(earliestDate)) return;
        if (latestDate && newDate > new Date(latestDate)) return;
        onSelect(newDate);
        onClose();
    };
    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 3, p: 2, minWidth: 320, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(19, 47, 76, 0.95)' : 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <IconButton size="small" onClick={() => setViewMonth(new Date(year, month - 1, 1))}><ChevronLeftIcon /></IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{monthNames[month]} {year}</Typography>
                <IconButton size="small" onClick={() => setViewMonth(new Date(year, month + 1, 1))}><ChevronRightIcon /></IconButton>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <Typography key={d} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem' }}>{d}</Typography>
                ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {Array.from({ length: firstDay }, (_, i) => <Box key={`e-${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = new Date(year, month, day);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const isSel = formatDateKey(date) === formatDateKey(selectedDate);
                    const isToday = isTodayDate(date);
                    const isDisabled = isWeekend || (earliestDate && date < new Date(earliestDate)) || (latestDate && date > new Date(latestDate));
                    return (
                        <Box key={day} onClick={() => !isDisabled && handleSelect(day)} sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontSize: '0.85rem', cursor: isDisabled ? 'default' : 'pointer', fontWeight: (isSel || isToday) ? 700 : 400, color: isDisabled ? 'text.disabled' : isSel ? 'primary.contrastText' : 'text.primary', backgroundColor: isSel ? 'primary.main' : 'transparent', border: isToday && !isSel ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent', transition: 'all 0.15s', '&:hover': !isDisabled ? { backgroundColor: isSel ? 'primary.dark' : 'action.hover' } : {} }}>{day}</Box>
                    );
                })}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button size="small" startIcon={<TodayIcon />} onClick={() => { const today = new Date(); onSelect(today.getDay() === 0 || today.getDay() === 6 ? getNextValidDate(today, -1) : today); onClose(); }} sx={{ textTransform: 'none', fontWeight: 600 }}>Go to Today</Button>
            </Box>
        </Dialog>
    );
};

const ExchangeFilter = ({ selectedExchanges, setSelectedExchanges, availableExchanges, isMobile }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleToggle = (exchange) => setSelectedExchanges((prev) => prev.includes(exchange) ? prev.filter((e) => e !== exchange) : [...prev, exchange]);
    return (
        <>
            <Button size="small" variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.875rem', minWidth: isMobile ? '100%' : 140, justifyContent: 'space-between', color: 'text.primary', borderColor: 'divider', height: 40, px: 1.5 }}>
                {selectedExchanges.length > 0 ? `Exchange (${selectedExchanges.length})` : 'Exchange'}<KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} PaperProps={{ sx: { mt: 0.5, borderRadius: 2, width: 200 } }}>
                {availableExchanges.map((exchange) => (
                    <MenuItem key={exchange} onClick={() => handleToggle(exchange)} dense sx={{ py: 0.25, px: 1, minHeight: 32 }}>
                        <Checkbox checked={selectedExchanges.includes(exchange)} size="small" sx={{ p: 0.25, mr: 0.75 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{exchange}</Typography>
                    </MenuItem>
                ))}
                {selectedExchanges.length > 0 && <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}><Button size="small" onClick={() => setSelectedExchanges([])} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>Clear all</Button></Box>}
            </Popover>
        </>
    );
};

const EpsSurpriseFilter = ({ selectedStatuses, setSelectedStatuses, isMobile, theme }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const statusConfig = {
        'Beat': { color: theme.palette.success.main, icon: <TrendingUpIcon sx={{ fontSize: '0.9rem' }} /> },
        'In-line': { color: theme.palette.text.secondary, icon: null },
        'Miss': { color: theme.palette.error.main, icon: <TrendingDownIcon sx={{ fontSize: '0.9rem' }} /> },
        'Pending': { color: theme.palette.warning.main, icon: <AccessTimeIcon sx={{ fontSize: '0.9rem' }} /> },
    };
    const handleToggle = (status) => setSelectedStatuses((prev) => prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]);
    return (
        <>
            <Button size="small" variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.875rem', minWidth: isMobile ? '100%' : 150, justifyContent: 'space-between', color: 'text.primary', borderColor: 'divider', height: 40, px: 1.5 }}>
                {selectedStatuses.length > 0 ? `EPS Result (${selectedStatuses.length})` : 'EPS Result'}<KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} PaperProps={{ sx: { mt: 0.5, borderRadius: 2, width: 190 } }}>
                {EPS_STATUSES.map((status) => {
                    const cfg = statusConfig[status]; return (
                        <MenuItem key={status} onClick={() => handleToggle(status)} dense sx={{ py: 0.25, px: 1, minHeight: 32 }}>
                            <Checkbox checked={selectedStatuses.includes(status)} size="small" sx={{ p: 0.25, mr: 0.75 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {cfg.icon && <Box sx={{ color: cfg.color, display: 'flex' }}>{cfg.icon}</Box>}
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: cfg.color, fontWeight: 600 }}>{status}</Typography>
                            </Box>
                        </MenuItem>
                    );
                })}
                {selectedStatuses.length > 0 && <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}><Button size="small" onClick={() => setSelectedStatuses([])} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>Clear all</Button></Box>}
            </Popover>
        </>
    );
};

const TimeFilter = ({ selectedTimes, setSelectedTimes, isMobile, theme }) => {
    const times = [
        { key: 'bmo', label: 'BMO', icon: <WbSunnyIcon sx={{ fontSize: '0.85rem' }} />, color: theme.palette.warning.main },
        { key: 'amc', label: 'AMC', icon: <DarkModeIcon sx={{ fontSize: '0.85rem' }} />, color: theme.palette.info.main },
    ];
    const handleToggle = (key) => setSelectedTimes((prev) => prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]);
    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {times.map((t) => {
                const active = selectedTimes.includes(t.key); return (
                    <Chip key={t.key} icon={t.icon} label={t.label} size="small" onClick={() => handleToggle(t.key)} variant={active ? 'filled' : 'outlined'}
                        sx={{ height: 32, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', backgroundColor: active ? `${t.color}25` : 'transparent', borderColor: active ? t.color : 'divider', color: active ? t.color : 'text.secondary', '& .MuiChip-icon': { color: active ? t.color : 'text.secondary' }, '&:hover': { backgroundColor: `${t.color}15` } }} />
                );
            })}
        </Box>
    );
};

const MarketCapFilter = ({ selectedBuckets, setSelectedBuckets, isMobile }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleToggle = (label) => setSelectedBuckets((prev) => prev.includes(label) ? prev.filter((b) => b !== label) : [...prev, label]);
    return (
        <>
            <Button size="small" variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.875rem', minWidth: isMobile ? '100%' : 140, justifyContent: 'space-between', color: 'text.primary', borderColor: 'divider', height: 40, px: 1.5 }}>
                {selectedBuckets.length > 0 ? `Mkt Cap (${selectedBuckets.length})` : 'Mkt Cap'}<KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} PaperProps={{ sx: { mt: 0.5, borderRadius: 2, width: 240 } }}>
                {MARKET_CAP_BUCKETS.map((bucket) => (
                    <MenuItem key={bucket.label} onClick={() => handleToggle(bucket.label)} dense sx={{ py: 0.25, px: 1, minHeight: 32 }}>
                        <Checkbox checked={selectedBuckets.includes(bucket.label)} size="small" sx={{ p: 0.25, mr: 0.75 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{bucket.label}</Typography>
                    </MenuItem>
                ))}
                {selectedBuckets.length > 0 && <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}><Button size="small" onClick={() => setSelectedBuckets([])} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>Clear all</Button></Box>}
            </Popover>
        </>
    );
};

const FilterBar = ({ searchQuery, setSearchQuery, selectedStatuses, setSelectedStatuses, selectedExchanges, setSelectedExchanges, availableExchanges, selectedTimes, setSelectedTimes, selectedBuckets, setSelectedBuckets, onClearFilters, hasActiveFilters, isMobile, theme }) => {
    const [showFilters, setShowFilters] = useState(false);
    const filterContent = (
        <Box sx={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 1.5, p: isMobile ? 2 : 0, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search ticker or company..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>, ...(searchQuery && { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> }) }}
                sx={{ minWidth: isMobile ? '100%' : 220, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' } }} />
            <TimeFilter selectedTimes={selectedTimes} setSelectedTimes={setSelectedTimes} isMobile={isMobile} theme={theme} />
            <EpsSurpriseFilter selectedStatuses={selectedStatuses} setSelectedStatuses={setSelectedStatuses} isMobile={isMobile} theme={theme} />
            <ExchangeFilter selectedExchanges={selectedExchanges} setSelectedExchanges={setSelectedExchanges} availableExchanges={availableExchanges} isMobile={isMobile} />
            <MarketCapFilter selectedBuckets={selectedBuckets} setSelectedBuckets={setSelectedBuckets} isMobile={isMobile} />
            {hasActiveFilters && <Button size="small" onClick={onClearFilters} startIcon={<CloseIcon fontSize="small" />} sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>Clear</Button>}
        </Box>
    );
    if (isMobile) {
        return (
            <Box>
                <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
                    <TextField size="small" fullWidth placeholder="Search ticker or company..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>, ...(searchQuery && { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> }) }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' } }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 0.75 }}>
                    <Badge color="primary" variant="dot" invisible={!(selectedStatuses.length > 0 || selectedExchanges.length > 0 || selectedTimes.length > 0 || selectedBuckets.length > 0)}>
                        <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 1.5 }}>
                            <FilterListIcon fontSize="small" />
                            <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>Filters</Typography>
                        </IconButton>
                    </Badge>
                    {hasActiveFilters && <Button size="small" onClick={onClearFilters} startIcon={<CloseIcon fontSize="small" />} sx={{ ml: 1, textTransform: 'none', fontWeight: 600 }}>Clear</Button>}
                </Box>
                <Collapse in={showFilters}>{filterContent}</Collapse>
            </Box>
        );
    }
    return <Box sx={{ px: 2, py: 1.5 }}>{filterContent}</Box>;
};

const EmptyState = ({ message }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 2 }}>
        <CalendarMonthIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>{message}</Typography>
        <Typography variant="body2" color="text.disabled">Try selecting a different date or adjusting your filters.</Typography>
    </Box>
);

const EarningsCalendar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [weekDates, setWeekDates] = useState([]);
    const [earningsData, setEarningsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [latestDate, setLatestDate] = useState(null);
    const [earliestDate, setEarliestDate] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedExchanges, setSelectedExchanges] = useState([]);
    const [selectedTimes, setSelectedTimes] = useState([]);
    const [selectedBuckets, setSelectedBuckets] = useState([]);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const titleTop = 'Earnings Calendar: ';
    const title = '';
    const info = '';

    useEffect(() => { setWeekDates(getWeekDates(selectedDate)); }, [selectedDate]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const getStartOfWeek = (date) => { const d = new Date(date); while (d.getDay() !== 1) d.setDate(d.getDate() - 1); d.setDate(d.getDate() - 7); return formatDateKey(d); };
                const getEndOfMonth = (date) => { const d = new Date(date); d.setMonth(d.getMonth() + 1); d.setDate(0); return formatDateKey(d); };
                const startDate = getStartOfWeek(new Date());
                const endDate = getEndOfMonth(new Date());
                const response = await axios.get(`/api/us-earnings-calendar?from=${startDate}&to=${endDate}`);
                const organizedData = {};
                let latest = null, earliest = null;
                response.data.data.forEach((item) => {
                    const date = formatDateKey(new Date(item.date));
                    if (!latest || date > latest) latest = date;
                    if (!earliest || date < earliest) earliest = date;
                    if (!organizedData[date]) organizedData[date] = [];
                    organizedData[date].push({ ...item, id: item._id });
                });
                setLatestDate(latest); setEarliestDate(earliest); setEarningsData(organizedData);
            } catch (error) { console.error('Error fetching earnings data:', error); }
            setLoading(false);
        };
        fetchData();
    }, []);

    const availableExchanges = useMemo(() => {
        const exchanges = new Set();
        Object.values(earningsData).forEach((dayEvents) => { dayEvents.forEach((e) => { if (e.exchange) exchanges.add(e.exchange); }); });
        return [...exchanges].sort();
    }, [earningsData]);

    const currentDayKey = formatDateKey(selectedDate);
    const rawDayData = useMemo(() => earningsData[currentDayKey] || [], [earningsData, currentDayKey]);

    const filteredData = useMemo(() => {
        let data = [...rawDayData];
        if (searchQuery.trim()) { const q = searchQuery.toLowerCase(); data = data.filter((e) => e.symbol.toLowerCase().includes(q) || (e.name && e.name.toLowerCase().includes(q))); }
        if (selectedStatuses.length > 0) data = data.filter((e) => selectedStatuses.includes(getSurpriseStatus(e.eps, e.epsEstimated, e.revenue)));
        if (selectedExchanges.length > 0) data = data.filter((e) => selectedExchanges.includes(e.exchange));
        if (selectedTimes.length > 0) data = data.filter((e) => selectedTimes.includes(e.time));
        if (selectedBuckets.length > 0) {
            data = data.filter((e) => {
                if (!e.marketCap) return false;
                return selectedBuckets.some((bucketLabel) => {
                    const bucket = MARKET_CAP_BUCKETS.find((b) => b.label === bucketLabel);
                    if (!bucket) return false;
                    return (bucket.min ? e.marketCap >= bucket.min : true) && (bucket.max ? e.marketCap < bucket.max : true);
                });
            });
        }
        return data;
    }, [rawDayData, searchQuery, selectedStatuses, selectedExchanges, selectedTimes, selectedBuckets]);

    const hasActiveFilters = searchQuery.trim() !== '' || selectedStatuses.length > 0 || selectedExchanges.length > 0 || selectedTimes.length > 0 || selectedBuckets.length > 0;
    const clearFilters = () => { setSearchQuery(''); setSelectedStatuses([]); setSelectedExchanges([]); setSelectedTimes([]); setSelectedBuckets([]); };

    const handleDateNavigation = useCallback((direction) => {
        let newDate;
        if (isMobile) { newDate = getNextValidDate(selectedDate, direction); }
        else { newDate = new Date(selectedDate); newDate.setDate(newDate.getDate() + direction * 7); }
        if (latestDate && newDate > new Date(latestDate)) return;
        if (earliestDate && newDate < new Date(earliestDate)) return;
        setSelectedDate(newDate);
    }, [selectedDate, isMobile, latestDate, earliestDate]);

    const isNextDisabled = latestDate && new Date(selectedDate) >= new Date(latestDate);
    const isPrevDisabled = earliestDate && new Date(selectedDate) <= new Date(earliestDate);

    const columns = [
        { field: 'symbol', headerName: 'Symbol', minWidth: 130, flex: 0.8, renderCell: (params) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><RouterLink to={`/asset/${params.value}`} style={{ color: theme.palette.primary.main, textDecoration: 'none', fontSize: '0.9rem', fontWeight: 700 }}>{params.value}</RouterLink>{isMegaCap(params.row.marketCap) && <Tooltip title="Mega Cap" arrow><StarIcon sx={{ fontSize: '0.85rem', color: theme.palette.warning.main }} /></Tooltip>}</Box>)},
        { field: 'name', headerName: 'Company', flex: 1.8, minWidth: 180, renderCell: (params) => { const pending = getSurpriseStatus(params.row.eps, params.row.epsEstimated, params.row.revenue) === 'Pending'; return <Typography variant="body2" sx={{ fontWeight: 500, fontStyle: pending ? 'italic' : 'normal', opacity: pending ? 0.7 : 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.value}</Typography>; } },
        { field: 'time', headerName: 'Time', minWidth: 90, flex: 0.5, renderCell: (params) => <TimeBadge time={params.value} theme={theme} /> },
        { field: 'exchange', headerName: 'Exchange', minWidth: 100, flex: 0.6, renderCell: (params) => <Chip label={params.value} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }} /> },
        { field: 'marketCap', headerName: 'Mkt Cap', minWidth: 100, flex: 0.7, renderCell: (params) => <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(params.value)}</Typography> },
        { field: 'eps', headerName: 'EPS', minWidth: 80, flex: 0.5, renderCell: (params) => { const pending = getSurpriseStatus(params.row.eps, params.row.epsEstimated, params.row.revenue) === 'Pending'; return <Typography variant="body2" sx={{ fontWeight: 600 }}>{pending ? '-' : formatEps(params.value)}</Typography>; } },
        { field: 'epsEstimated', headerName: 'EPS Est.', minWidth: 80, flex: 0.5, renderCell: (params) => <Typography variant="body2" color="text.secondary">{formatEps(params.value)}</Typography> },
        { field: 'epsSurprise', headerName: 'EPS Actual', minWidth: 140, flex: 0.8, sortComparator: (a, b) => { if (a == null) return 1; if (b == null) return -1; return a - b; }, valueGetter: (params) => getEpsSurprise(params.row.eps, params.row.epsEstimated), renderCell: (params) => { const pending = getSurpriseStatus(params.row.eps, params.row.epsEstimated, params.row.revenue) === 'Pending'; return <SurpriseChip actual={params.row.eps} estimated={params.row.epsEstimated} format="eps" theme={theme} isPending={pending} />; } },
        { field: 'revenue', headerName: 'Revenue', minWidth: 100, flex: 0.7, renderCell: (params) => { const pending = getSurpriseStatus(params.row.eps, params.row.epsEstimated, params.row.revenue) === 'Pending'; return <Typography variant="body2" sx={{ fontWeight: 600 }}>{pending ? '-' : formatCurrency(params.value)}</Typography>; } },
        { field: 'revenueEstimated', headerName: 'Rev. Est.', minWidth: 100, flex: 0.7, renderCell: (params) => <Typography variant="body2" color="text.secondary">{formatCurrency(params.value)}</Typography> },
        { field: 'revSurprise', headerName: 'Rev. Actual', minWidth: 150, flex: 0.9, valueGetter: (params) => getRevenueSurprise(params.row.revenue, params.row.revenueEstimated), renderCell: (params) => { const pending = getSurpriseStatus(params.row.eps, params.row.epsEstimated, params.row.revenue) === 'Pending'; return <SurpriseChip actual={params.row.revenue} estimated={params.row.revenueEstimated} format="revenue" theme={theme} isPending={pending} />; } },
        { field: 'price', headerName: 'Price', minWidth: 80, flex: 0.5, renderCell: (params) => <Typography variant="body2">{params.value ? `$${params.value.toFixed(2)}` : '-'}</Typography> },
    ];

    if (loading) {
        return (<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 8, gap: 2 }}><CircularProgress /><Typography variant="body2" color="text.secondary">Loading earnings data...</Typography></Box>);
    }

    const renderDateNavigation = () => {
        if (isMobile) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <IconButton onClick={() => handleDateNavigation(-1)} disabled={isPrevDisabled} size="small"><ChevronLeftIcon /></IconButton>
                    <Box onClick={() => setDatePickerOpen(true)} sx={{ textAlign: 'center', cursor: 'pointer', '&:active': { opacity: 0.7 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatMobileDate(selectedDate)}</Typography>
                            <CalendarMonthIcon fontSize="small" color="primary" />
                        </Box>
                        <Typography variant="caption" color="text.secondary">{rawDayData.length} Earnings{hasActiveFilters ? ` (${filteredData.length} shown)` : ''}</Typography>
                        {isTodayDate(selectedDate) && <Chip label="Today" size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />}
                    </Box>
                    <IconButton onClick={() => handleDateNavigation(1)} disabled={isNextDisabled} size="small"><ChevronRightIcon /></IconButton>
                </Box>
            );
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'stretch', borderBottom: 1, borderColor: 'divider' }}>
                <IconButton onClick={() => handleDateNavigation(-1)} disabled={isPrevDisabled} sx={{ borderRadius: 0, px: 2, '&:hover': { backgroundColor: 'action.hover' }, opacity: isPrevDisabled ? 0.5 : 1 }}><ChevronLeftIcon /></IconButton>
                {weekDates.map((day) => {
                    const dayData = earningsData[day.fullDate] || [];
                    const isSelected = formatDateKey(selectedDate) === day.fullDate;
                    const isToday = isTodayDate(day.date);
                    return (
                        <Box key={day.fullDate} onClick={() => setSelectedDate(day.date)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 1.5, cursor: 'pointer', position: 'relative', backgroundColor: isSelected ? 'primary.main' : 'transparent', transition: 'background-color 0.15s', '&:hover': { backgroundColor: isSelected ? 'primary.dark' : 'action.hover' } }}>
                            {isToday && !isSelected && <Box sx={{ position: 'absolute', top: 4, right: 4, width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main' }} />}
                            <Typography sx={{ color: isSelected ? 'primary.contrastText' : 'text.primary', fontWeight: isToday ? 700 : 500, fontSize: '0.875rem' }}>{day.formatted}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <Typography variant="caption" sx={{ color: isSelected ? 'primary.contrastText' : 'text.secondary', fontSize: '0.75rem' }}>{dayData.length} Earnings</Typography>
                                {isToday && <Chip label="Today" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : 'primary.main', color: isSelected ? 'primary.contrastText' : '#fff' }} />}
                            </Box>
                        </Box>
                    );
                })}
                <Tooltip title="Jump to date" arrow><IconButton onClick={() => setDatePickerOpen(true)} sx={{ borderRadius: 0, px: 1.5, borderLeft: 1, borderColor: 'divider', '&:hover': { backgroundColor: 'action.hover' } }}><CalendarMonthIcon fontSize="small" /></IconButton></Tooltip>
                <IconButton onClick={() => handleDateNavigation(1)} disabled={isNextDisabled} sx={{ borderRadius: 0, px: 2, '&:hover': { backgroundColor: 'action.hover' }, opacity: isNextDisabled ? 0.5 : 1 }}><ChevronRightIcon /></IconButton>
            </Box>
        );
    };

    return (
        <Container maxWidth="xl">
            <TitleAndInfo titleTop={titleTop} title={title} info={info} />
            <Paper elevation={3} variant="stats" sx={{ overflow: 'hidden', mb: 2 }}>
                {renderDateNavigation()}
                <SummaryStatsBar data={rawDayData} theme={theme} isMobile={isMobile} />
                <FilterBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} selectedStatuses={selectedStatuses} setSelectedStatuses={setSelectedStatuses} selectedExchanges={selectedExchanges} setSelectedExchanges={setSelectedExchanges} availableExchanges={availableExchanges} selectedTimes={selectedTimes} setSelectedTimes={setSelectedTimes} selectedBuckets={selectedBuckets} setSelectedBuckets={setSelectedBuckets} onClearFilters={clearFilters} hasActiveFilters={hasActiveFilters} isMobile={isMobile} theme={theme} />
            </Paper>
            {filteredData.length === 0 ? (
                <EmptyState message={rawDayData.length === 0 ? 'No earnings scheduled for this day' : 'No earnings match your filters'} />
            ) : isMobile ? (
                <Box sx={{ pb: 4 }}>
                    {[...filteredData].sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0)).map((event) => (<MobileEventCard key={event.id} event={event} theme={theme} />))}
                </Box>
            ) : (
                <Box sx={{ height: 650, width: '100%' }}>
                    <DataGrid rows={filteredData} columns={columns} disableColumnMenu
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: 'marketCap', sort: 'desc' }] } }}
                        pageSizeOptions={[10, 25, 50]}
                        getRowClassName={(params) => { const pending = getSurpriseStatus(params.row.eps, params.row.epsEstimated, params.row.revenue) === 'Pending'; const mega = isMegaCap(params.row.marketCap); return `${pending ? 'pending-row' : ''} ${mega ? 'mega-cap-row' : ''}`.trim(); }}
                        sx={{ '& .pending-row': { opacity: 0.6, fontStyle: 'italic' }, '& .mega-cap-row': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.04)' : 'rgba(255, 167, 38, 0.06)' }, '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover } }}
                    />
                </Box>
            )}
            <DatePickerDialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)} onSelect={(date) => setSelectedDate(date)} selectedDate={selectedDate} earliestDate={earliestDate} latestDate={latestDate} theme={theme} />
        </Container>
    );
};

export default EarningsCalendar;